/**
 * How a shop is presented: its own mark, a status anyone can read, and a card
 * title without the words the shelf already says.
 */

/**
 * Each shop's own small logo — its favicon, which is the mark the shop itself
 * designed to be read at this size. Served from this app's origin and
 * precached by the service worker like every other static asset, never
 * hotlinked: that would be a request that fails in the aisle and one more
 * thing telling a retailer the household is looking.
 */
const LOGOS: Record<string, string> = {
  lidl: '/shops/lidl.svg',
  ah: '/shops/ah.png',
  ekoplaza: '/shops/ekoplaza.png',
  jumbo: '/shops/jumbo.png',
};

export function shopLogo(code: string): string | null {
  return LOGOS[code] ?? null;
}

/**
 * Why a shop did not answer, in Dutch. The server's reason is a stable code for
 * the client to branch on (`RUN_REASONS` in `api/src/adapters/index.ts`), never
 * display copy — `niet bereikbaar (adapter_failed)` used to reach the screen.
 * Each line says what to do about it, which for all of these is "later".
 */
export function shopProblem(reason: string | undefined): string {
  switch (reason) {
    case 'timeout':
      return 'reageerde te traag — probeer het zo opnieuw';
    case 'rate_limited':
      return 'even te druk — probeer het zo opnieuw';
    case 'unsupported':
      return 'hier kan niet gezocht worden';
    case 'adapter_degraded':
    case 'adapter_failed':
      return 'reageert nu niet — probeer het later';
    default:
      return 'niet bereikbaar — probeer het later';
  }
}

/** The house-brand word each shop prints at the front of its own titles. */
const HOUSE_BRAND: Record<string, string> = {
  ah: 'AH',
  jumbo: 'Jumbo',
  ekoplaza: 'Ekoplaza',
};

const squash = (text: string) => text.toLocaleLowerCase('nl-NL').replace(/\s+/g, '');

/**
 * A card title without what its shelf and its own meta line already say:
 * "Jumbo Gewassen Spinazie 400 g" on Jumbo's shelf, above "400 g", is
 * "Gewassen Spinazie". Display only — the stored name and the accessible name
 * keep every word. Never strips a title to nothing.
 */
export function shelfTitle(name: string, store: string, sizeLabel: string | null): string {
  let title = name.trim();
  const brand = HOUSE_BRAND[store];
  if (brand) {
    const prefix = new RegExp(`^${brand}\\s+`, 'i');
    if (prefix.test(title)) title = title.replace(prefix, '');
  }
  if (sizeLabel) {
    const words = title.split(/\s+/);
    // The size is the last one or two words ("400 g", "1L", "6 x 1,5 l").
    for (let take = Math.min(4, words.length - 1); take >= 1; take -= 1) {
      if (squash(words.slice(-take).join(' ')) === squash(sizeLabel)) {
        title = words.slice(0, -take).join(' ');
        break;
      }
    }
  }
  title = title.trim();
  if (!title) return name;
  return title.charAt(0).toLocaleUpperCase('nl-NL') + title.slice(1);
}
